import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { AbstractAppConfig } from '../../../../app.config';
import { HttpErrorService, OrderService } from '../../../services';
import { ShowCondition } from '../../../directives/conditional-show/domain/conditional-show.model';
import { catchError, map, tap } from 'rxjs/operators';
import { CaseEventData, CaseEventTrigger, CasePrintDocument, CaseView, Draft } from '../../../domain';
import { WizardPage } from '../domain';
import { WizardPageFieldToCaseFieldMapper } from './wizard-page-field-to-case-field.mapper';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { plainToClass } from 'class-transformer';

@Injectable()
export class CasesService {

  public static readonly V2_MEDIATYPE_CASE_VIEW = 'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-case-view.v2+json';
  public static readonly V2_MEDIATYPE_START_CASE_TRIGGER =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-start-case-trigger.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_START_EVENT_TRIGGER =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-start-event-trigger.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_START_DRAFT_TRIGGER =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-start-draft-trigger.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_CASE_DATA_VALIDATE =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.case-data-validate.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_CREATE_EVENT =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.create-event.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_CREATE_CASE =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.create-case.v2+json;charset=UTF-8';

  public static readonly QC_V2_MEDIATYPE_CASE_VIEW = 'application/vnd.app.quickcase.store.data.api.ui-case-view.v2+json';
  public static readonly QC_V2_MEDIATYPE_START_CASE_TRIGGER =
    'application/vnd.app.quickcase.store.data.api.ui-start-case-trigger.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_START_EVENT_TRIGGER =
    'application/vnd.app.quickcase.store.data.api.ui-start-event-trigger.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_START_DRAFT_TRIGGER =
    'application/vnd.app.quickcase.store.data.api.ui-start-draft-trigger.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_CASE_DATA_VALIDATE =
    'application/vnd.app.quickcase.store.data.api.case-data-validate.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_CREATE_EVENT =
    'application/vnd.app.quickcase.store.data.api.create-event.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_CREATE_CASE =
    'application/vnd.app.quickcase.store.data.api.create-case.v2+json;charset=UTF-8';

  /**
   *
   * @type {(caseId:string)=>"../../Observable".Observable<Case>}
   * @deprecated Use `CasesService::getCaseView` instead
   */
  get = this.getCaseView;

  constructor(
    private httpClient: HttpClient,
    private appConfig: AbstractAppConfig,
    private orderService: OrderService,
    private errorService: HttpErrorService,
    private wizardPageFieldToCaseFieldMapper: WizardPageFieldToCaseFieldMapper
  ) {
  }

  getCaseView(caseId: string): Observable<CaseView> {
    const url = `${this.appConfig.getCaseDataUrl()}/internal/cases/${caseId}`;
    const headers = new HttpHeaders({
      'Accept': [CasesService.V2_MEDIATYPE_CASE_VIEW, CasesService.QC_V2_MEDIATYPE_CASE_VIEW],
    });
    return this.httpClient.get<CaseView>(url, {headers: headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  getEventTrigger(caseTypeId: string,
                  eventTriggerId: string,
                  caseId?: string,
                  ignoreWarning?: string): Observable<CaseEventTrigger> {
    ignoreWarning = undefined !== ignoreWarning ? ignoreWarning : 'false';

    let url = this.buildEventTriggerUrl(caseTypeId, eventTriggerId, caseId, ignoreWarning);

    let headers;
    if (Draft.isDraft(caseId)) {
      headers = new HttpHeaders({
        'Accept': [CasesService.V2_MEDIATYPE_START_DRAFT_TRIGGER, CasesService.QC_V2_MEDIATYPE_START_DRAFT_TRIGGER],
      });
    } else if (caseId !== undefined && caseId !== null) {
      headers = new HttpHeaders({
        'Accept': [CasesService.V2_MEDIATYPE_START_EVENT_TRIGGER, CasesService.QC_V2_MEDIATYPE_START_EVENT_TRIGGER],
      });
    } else {
      headers = new HttpHeaders({
        'Accept': [CasesService.V2_MEDIATYPE_START_CASE_TRIGGER, CasesService.QC_V2_MEDIATYPE_START_CASE_TRIGGER],
      });
    }

    return this.httpClient
      .get<CaseEventTrigger>(url, {headers: headers})
      .pipe(
        map((p: Object) => plainToClass(CaseEventTrigger, p)),
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        }),
        tap(eventTrigger => this.initialiseEventTrigger(eventTrigger))
      );
  }

  createEvent(caseDetails: CaseView, eventData: CaseEventData): Observable<object> {
    const caseId = caseDetails.case_id;
    const url = this.appConfig.getCaseDataUrl() + `/internal/cases/${caseId}/events`;

    let headers = new HttpHeaders({
      'Accept': [CasesService.V2_MEDIATYPE_CREATE_EVENT, CasesService.QC_V2_MEDIATYPE_CREATE_EVENT],
    });

    return this.httpClient
      .post<object>(url, eventData, {observe: 'response', headers: headers})
      .pipe(
        map(response => this.processResponse(response)),
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  validateCase(ctid: string, eventData: CaseEventData, pageId: string): Observable<object> {
    const pageIdString = pageId ? '?pageId=' + pageId : '';
    const url = this.appConfig.getCaseDataUrl()
      + `/internal/case-types/${ctid}/validate${pageIdString}`;

    let headers = new HttpHeaders({
      'Accept': [CasesService.V2_MEDIATYPE_CASE_DATA_VALIDATE, CasesService.QC_V2_MEDIATYPE_CASE_DATA_VALIDATE],
    });

    return this.httpClient
      .post<object>(url, eventData, {headers: headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  createCase(ctid: string, eventData: CaseEventData): Observable<object> {
    let ignoreWarning = 'false';

    if (eventData.ignore_warning) {
      ignoreWarning = 'true';
    }
    const url = this.appConfig.getCaseDataUrl()
    + `/internal/case-types/${ctid}/cases?ignore-warning=${ignoreWarning}`;

    let headers = new HttpHeaders({
      'Accept': [CasesService.V2_MEDIATYPE_CREATE_CASE, CasesService.QC_V2_MEDIATYPE_CREATE_CASE],
    });

    return this.httpClient
      .post<object>(url, eventData, {observe: 'response', headers: headers})
      .pipe(
        map(response => this.processResponse(response)),
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  getPrintDocuments(jurisdictionId: string, caseTypeId: string, caseId: string): Observable<CasePrintDocument[]> {
    const url = this.appConfig.getCaseDataUrl()
      + `/caseworkers/:uid`
      + `/jurisdictions/${jurisdictionId}`
      + `/case-types/${caseTypeId}`
      + `/cases/${caseId}`
      + `/documents`;

    return this.httpClient
      .get<CasePrintDocument[]>(url)
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  private buildEventTriggerUrl(caseTypeId: string,
                               eventTriggerId: string,
                               caseId?: string,
                               ignoreWarning?: string): string {
    let url = this.appConfig.getCaseDataUrl() + `/internal`;

    if (Draft.isDraft(caseId)) {
      url += `/drafts/${caseId}`
        + `/event-trigger`
        + `?ignore-warning=${ignoreWarning}`;
    } else if (caseTypeId === undefined || caseTypeId === null) {
      url += `/cases/${caseId}`
        + `/event-triggers/${eventTriggerId}`
        + `?ignore-warning=${ignoreWarning}`;
    } else {
      url += `/case-types/${caseTypeId}`
        + `/event-triggers/${eventTriggerId}`
        + `?ignore-warning=${ignoreWarning}`;
    }

    return url;
  }

  private processResponse(response: HttpResponse<object>) {
    if (response.headers && response.headers.get('content-type') && response.headers.get('content-type').match(/application\/.*json/)) {
      return response.body;
    }
    return {'id': ''};
  }

  private initialiseEventTrigger(eventTrigger: CaseEventTrigger) {
    if (!eventTrigger.wizard_pages) {
      eventTrigger.wizard_pages = [];
    }

    eventTrigger.wizard_pages.forEach((wizardPage: WizardPage) => {
      wizardPage.parsedShowCondition = new ShowCondition(wizardPage.show_condition);
      wizardPage.case_fields = this.orderService
        .deepSort(this.wizardPageFieldToCaseFieldMapper.mapAll(wizardPage.wizard_page_fields, eventTrigger.case_fields));
    });
  }

}
